import {
  DEFAULT_BE_THRESHOLD,
  computeDailyPnlSeries,
  computeExpectancy,
  computeGreenDayPercent,
  computeProfitFactor,
  computeWinLossStats,
  median,
} from "../statsUtils";
import { cleanCreatedTimestamp } from "./dateUtils";

function round(value, decimals = 2) {
  const factor = 10 ** decimals;
  return Math.round((Number(value) || 0) * factor) / factor;
}

export function getInsightDate(trade) {
  if (trade?.year != null && trade?.month != null && trade?.day != null) {
    const date = new Date(Number(trade.year), Number(trade.month) - 1, Number(trade.day));
    if (!Number.isNaN(date.getTime())) return date;
  }
  if (trade?.created) {
    const date = new Date(cleanCreatedTimestamp(trade.created));
    if (!Number.isNaN(date.getTime())) return date;
  }
  return null;
}

export function getInsightDateKey(trade) {
  const date = getInsightDate(trade);
  if (!date) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function computePerformanceSnapshot(trades, beThreshold = DEFAULT_BE_THRESHOLD) {
  const items = Array.isArray(trades) ? trades : [];
  const stats = computeWinLossStats(items, beThreshold);
  const pnlValues = items.map((trade) => Number(trade.pnl) || 0);
  const rrValues = items
    .map((trade) => Number(trade.rr))
    .filter((value) => Number.isFinite(value));
  const dailySeries = computeDailyPnlSeries(items);

  return {
    count: stats.total,
    wins: stats.wins,
    losses: stats.losses,
    breakeven: stats.breakeven,
    winRate: stats.winRate,
    totalPnl: stats.sum,
    averagePnl: stats.total ? round(stats.sum / stats.total) : 0,
    medianPnl: round(median(pnlValues)),
    expectancy: computeExpectancy(items),
    profitFactor: computeProfitFactor(items),
    averageRr: rrValues.length ? round(rrValues.reduce((sum, value) => sum + value, 0) / rrValues.length) : null,
    greenDayPercent: computeGreenDayPercent(dailySeries),
    tradingDays: dailySeries.length,
  };
}

export function computeRecentForm(trades, sampleSize = 5, beThreshold = DEFAULT_BE_THRESHOLD) {
  const sorted = [...(trades || [])].sort((a, b) => {
    const aDate = getInsightDate(a)?.getTime() || 0;
    const bDate = getInsightDate(b)?.getTime() || 0;
    return aDate - bDate;
  });
  const recent = sorted.slice(-sampleSize);
  const previous = sorted.slice(-(sampleSize * 2), -sampleSize);
  const recentSnapshot = computePerformanceSnapshot(recent, beThreshold);
  const previousSnapshot = computePerformanceSnapshot(previous, beThreshold);

  return {
    recent: recentSnapshot,
    previous: previousSnapshot,
    hasComparison: previous.length > 0,
    pnlChange: round(recentSnapshot.totalPnl - previousSnapshot.totalPnl),
    winRateChange: round(recentSnapshot.winRate - previousSnapshot.winRate, 1),
  };
}

export function computeTradePace(trades, referenceDate = new Date()) {
  const counts = new Map();
  (trades || []).forEach((trade) => {
    const key = getInsightDateKey(trade);
    if (!key) return;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  const currentKey = `${referenceDate.getFullYear()}-${String(referenceDate.getMonth() + 1).padStart(2, "0")}-${String(referenceDate.getDate()).padStart(2, "0")}`;
  const today = counts.get(currentKey) || 0;
  const comparisonDays = [...counts.entries()].filter(([key]) => key !== currentKey).map(([, count]) => count);
  const typical = comparisonDays.length
    ? round(comparisonDays.reduce((sum, value) => sum + value, 0) / comparisonDays.length, 1)
    : 0;

  return {
    today,
    typical,
    status: typical > 0 && today >= Math.max(3, typical * 1.5) ? "Above normal" : today > 0 ? "Normal" : "No trades yet",
  };
}

export function computeDrawdownInsights(trades) {
  const dailySeries = computeDailyPnlSeries(trades || []);
  let cumulativePnl = 0;
  let peak = 0;
  let maxDrawdown = 0;
  let recoveryStartIndex = null;
  let longestRecoveryDays = 0;

  const series = dailySeries.map((point, index) => {
    cumulativePnl += point.pnl;
    if (cumulativePnl >= peak) {
      peak = cumulativePnl;
      if (recoveryStartIndex !== null) {
        longestRecoveryDays = Math.max(longestRecoveryDays, index - recoveryStartIndex);
        recoveryStartIndex = null;
      }
    }
    const drawdown = Math.max(0, peak - cumulativePnl);
    if (drawdown > 0 && recoveryStartIndex === null) recoveryStartIndex = index;
    maxDrawdown = Math.max(maxDrawdown, drawdown);
    return {
      ...point,
      cumulativePnl: round(cumulativePnl),
      drawdown: round(drawdown),
    };
  });

  if (recoveryStartIndex !== null && series.length > 0) {
    longestRecoveryDays = Math.max(longestRecoveryDays, series.length - 1 - recoveryStartIndex);
  }

  return {
    series,
    maxDrawdown: round(maxDrawdown),
    currentDrawdown: series.length ? series[series.length - 1].drawdown : 0,
    longestRecoveryDays,
    atEquityHigh: series.length > 0 && series[series.length - 1].drawdown === 0,
  };
}

export function parseDurationMinutes(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (!value || typeof value !== "string") return null;
  const normalized = value.toLowerCase().trim();
  if (/^\d+(\.\d+)?$/.test(normalized)) return Number(normalized);
  const months = Number(normalized.match(/(\d+(?:\.\d+)?)\s*mo/)?.[1] || 0);
  const days = Number(normalized.match(/(\d+(?:\.\d+)?)\s*d/)?.[1] || 0);
  const hours = Number(normalized.match(/(\d+(?:\.\d+)?)\s*h(?:r|rs|our|ours)?/)?.[1] || 0);
  const minutes = Number(normalized.match(/(\d+(?:\.\d+)?)\s*m(?!o)(?:in|ins|inute|inutes)?/)?.[1] || 0);
  const total = months * 30 * 24 * 60 + days * 24 * 60 + hours * 60 + minutes;
  return total > 0 ? total : null;
}

export function getDurationBucket(trade) {
  const minutes = parseDurationMinutes(trade?.duration);
  if (minutes == null) return "Not recorded";
  if (minutes < 15) return "Under 15m";
  if (minutes < 30) return "15-29m";
  if (minutes < 60) return "30-59m";
  if (minutes < 120) return "1-2h";
  if (minutes < 24 * 60) return "2h+";
  return "Multi-day";
}

export function getSampleConfidence(count, minimumSample = 3) {
  if (count < minimumSample) return { label: "Small sample", level: "small" };
  if (count < minimumSample * 2) return { label: "Early", level: "early" };
  if (count < minimumSample * 4) return { label: "Developing", level: "developing" };
  return { label: "Established", level: "established" };
}

export function buildGroupedPerformance(
  trades,
  getLabel,
  { beThreshold = DEFAULT_BE_THRESHOLD, minimumSample = 1, includeSmallSamples = true } = {}
) {
  const groups = new Map();
  (trades || []).forEach((trade) => {
    const rawLabel = getLabel(trade);
    const label = typeof rawLabel === "string" ? rawLabel.trim() : rawLabel;
    if (!label) return;
    const key = String(label).toLowerCase();
    const current = groups.get(key) || { label: String(label), trades: [] };
    current.trades.push(trade);
    groups.set(key, current);
  });

  return [...groups.values()]
    .map((group) => {
      const snapshot = computePerformanceSnapshot(group.trades, beThreshold);
      return {
        label: group.label,
        ...snapshot,
        confidence: getSampleConfidence(snapshot.count, minimumSample),
        qualified: snapshot.count >= minimumSample,
      };
    })
    .filter((group) => includeSmallSamples || group.qualified)
    .sort((a, b) => b.expectancy - a.expectancy || b.count - a.count);
}

export function buildWeekdayHourHeatmap(trades, beThreshold = DEFAULT_BE_THRESHOLD) {
  const groups = new Map();
  (trades || []).forEach((trade) => {
    const date = getInsightDate(trade);
    const hour = Number.parseInt(String(trade.entryTime || "").split(":")[0], 10);
    if (!date || !Number.isInteger(hour) || hour < 0 || hour > 23) return;
    const weekday = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][date.getDay()];
    const key = `${weekday}-${hour}`;
    const current = groups.get(key) || { weekday, hour, trades: [] };
    current.trades.push(trade);
    groups.set(key, current);
  });
  return [...groups.values()].map((group) => ({
    ...group,
    ...computePerformanceSnapshot(group.trades, beThreshold),
  }));
}

export function comparePerformanceSnapshots(current, previous) {
  if (!current || !previous) return null;
  return {
    pnl: round(current.totalPnl - previous.totalPnl),
    winRate: round(current.winRate - previous.winRate, 1),
    expectancy: round(current.expectancy - previous.expectancy),
    averagePnl: round(current.averagePnl - previous.averagePnl),
    tradeCount: current.count - previous.count,
  };
}
